/** importing libraries  **/
import React, { useEffect, useState } from "react";
import { Navigate, useLocation } from "react-router-dom";
import { buildLoginPath } from "../utils/returnPath";

interface Props {
    children: React.ReactNode;
}

const ProtectedRoute: React.FC<Props> = ({ children }) => {
    const location = useLocation();
    // localStorage access can throw (e.g. Safari private browsing, storage
    // disabled by policy). Treat that the same as "no token" - redirect to
    // login rather than crashing the app - matching the same guard
    // AuthUtils.getAuthToken() already applies to this exact key.
    let token: string | null = null;
    try {
        token = localStorage.getItem("access_token");
    } catch {
        token = null;
    }
    const isAuthenticated = Boolean(token && token.trim().length > 0);

    // Check if there's an invitation token in the URL (for anonymous access)
    const searchParams = new URLSearchParams(location.search);
    const invitationToken = searchParams.get('token') || searchParams.get('invitationToken');
    const hasInvitationToken = Boolean(invitationToken && invitationToken.trim().length > 0);

    // Check if we're on a route that accepts invitation tokens
    const isOneTimeAuditsRoute = location.pathname === '/one-time-audits' || location.pathname.includes('/one-time-audits');

    // Allow access if authenticated OR if there's a valid invitation token on the one-time-audits route
    const hasAccess = isAuthenticated || (hasInvitationToken && isOneTimeAuditsRoute);

    const [showLoading, setShowLoading] = useState(true);

    useEffect(() => {
        let timer: ReturnType<typeof setTimeout> | null = null;

        if (!hasAccess) {
            timer = setTimeout(() => {
                setShowLoading(false);
            }, 2500);
        } else {
            setShowLoading(false);
        }

        return () => {
            if (timer) {
                clearTimeout(timer);
            }
        };
    }, [hasAccess, location.search, location.pathname]);

    return hasAccess ? (
        <>{children}</>
    ) : (
        <Navigate to={buildLoginPath(location)} replace={true} />
    );
};

export default ProtectedRoute;
